import React, { useMemo, useState } from 'react';
import { Button, Stack } from '@mui/material';
import { useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { openDialog } from '@/features/dialog/dialogSlice';
import { SCOPES } from '@/features/config/scopes';
import { AdminDataGrid } from '../../shared/AdminDataGrid';
import { useAdminListParams } from '../../shared/useAdminListParams';
import { useListFormFieldConfigsQuery } from '../api/formFieldConfigsApi';
import {
	COPY_FORM_FIELD_CONFIGS_DIALOG,
	CopyFormFieldConfigsDialog,
} from '../CopyFormFieldConfigsDialog';
import {
	SHARE_FORM_FIELD_CONFIGS_DIALOG,
	ShareFormFieldConfigsDialog,
} from '../ShareFormFieldConfigsDialog';
import { useFormFieldConfigScope } from '../useFormFieldConfigScope';
import { CONFIG_TYPE, isScopeChosen } from '../scopes';
import { formFieldConfigColumns } from './columns';
import { ResetFormFieldConfigsButton } from './ResetFormFieldConfigsButton';

// Text settings edited in their cells
const TEXT_FIELDS = ['label', 'helperText', 'description'];

/**
 * The form field configs the page's scope sees, one per field, showing the form's unsaved
 * values. Text cells and switches change the Formik values; the footer saves them; shared rows
 * can't be edited. Ticked rows can be copied (CopyFormFieldConfigsDialog), shared when they're
 * broker group rows (ShareFormFieldConfigsDialog) or reset (ResetFormFieldConfigsButton) once
 * there are no unsaved changes. Rows are ticked by field name, so a tick stays on the field
 * when a save gives it a new config id, and Copy, Share and Reset send the current ids.
 */
export const FormFieldConfigsTable = () => {
	const dispatch = useDispatch();
	const { scope } = useFormFieldConfigScope();
	const { values, dirty, setFieldValue } = useFormikContext();
	const { gridProps } = useAdminListParams('formFieldConfigs');
	// currentData, not data: data keeps the previous scope's rows while a new scope loads
	const {
		currentData: configs,
		isFetching,
		isError,
	} = useListFormFieldConfigsQuery(
		{ ...scope, configType: CONFIG_TYPE },
		// Empty until a ledger or broker group is picked
		{ skip: !isScopeChosen(scope) },
	);
	const [selectedNames, setSelectedNames] = useState([]);
	const isCopyOpen = useSelector((state) => state.dialog.open[COPY_FORM_FIELD_CONFIGS_DIALOG]);
	const isShareOpen = useSelector((state) => state.dialog.open[SHARE_FORM_FIELD_CONFIGS_DIALOG]);

	const rows = useMemo(
		() => (configs ?? []).map((config) => ({ ...config, ...values[config.id] })),
		[configs, values],
	);
	const selectedConfigs = rows.filter((row) => selectedNames.includes(row.name));
	// Only broker group configs can be shared; ledger and default rows are copied instead
	const shareableConfigs = selectedConfigs.filter((row) => row.scope === SCOPES.BROKER_GROUP);

	// Keeps edited text in the form
	const handleRowUpdate = (newRow, oldRow) => {
		TEXT_FIELDS.filter((field) => newRow[field] !== oldRow[field]).forEach((field) =>
			setFieldValue(`${oldRow.id}.${field}`, newRow[field]),
		);
		return newRow;
	};

	return (
		<>
			<AdminDataGrid
				table='formFieldConfigs'
				columns={formFieldConfigColumns.filter(
					(column) =>
						// Every default row belongs to the default
						!(scope.scope === SCOPES.DEFAULT && column.field === 'scope') &&
						// Only broker group configs are shared
						!(scope.scope !== SCOPES.BROKER_GROUP && column.field === 'sharedWith'),
				)}
				rows={rows}
				getRowId={(row) => row.name}
				isLoading={isFetching}
				isError={isError}
				objectType='form field configs'
				minHeight='75vh'
				processRowUpdate={handleRowUpdate}
				// A shared row follows its original and can't be edited
				isCellEditable={(params) => params.colDef.editable && !params.row.sharedFrom}
				checkboxSelection
				disableRowSelectionOnClick
				rowSelectionModel={selectedNames}
				onRowSelectionModelChange={setSelectedNames}
				toolbarActions={
					<Stack direction='row' spacing={1} sx={{ ml: 'auto' }}>
						<ResetFormFieldConfigsButton configs={selectedConfigs} disabled={dirty} />
						{shareableConfigs.length > 0 && (
							<Button
								variant='table_actions'
								disabled={dirty}
								onClick={() =>
									dispatch(
										openDialog({
											dialogName: SHARE_FORM_FIELD_CONFIGS_DIALOG,
											data: {
												docIds: shareableConfigs.map((config) => config.id),
												skipped:
													selectedConfigs.length -
													shareableConfigs.length,
											},
										}),
									)
								}
							>
								{`Share (${shareableConfigs.length})`}
							</Button>
						)}
						{selectedConfigs.length > 0 && (
							<Button
								variant='table_actions'
								disabled={dirty}
								onClick={() =>
									dispatch(
										openDialog({
											dialogName: COPY_FORM_FIELD_CONFIGS_DIALOG,
											data: {
												docIds: selectedConfigs.map((config) => config.id),
											},
										}),
									)
								}
							>
								{`Copy (${selectedConfigs.length})`}
							</Button>
						)}
					</Stack>
				}
				{...gridProps}
			/>
			{/* Outside the grid, so typing in its pickers isn't caught by the grid's keyboard handling */}
			{isCopyOpen && <CopyFormFieldConfigsDialog />}
			{isShareOpen && <ShareFormFieldConfigsDialog />}
		</>
	);
};

FormFieldConfigsTable.displayName = 'FormFieldConfigsTable';
